import React, { useEffect, useState } from 'react';
import { Tooltip, useMediaQuery } from '@mui/material';
import styled from 'styled-components';
import { LuPanelLeft } from 'react-icons/lu';
import {
  DASHBOARD_SIDEBAR_DESKTOP_MQL,
  expandDesktopDashboardSidebar,
  MILLET_DESKTOP_SIDEBAR_STATE_EVENT,
  readDashboardSidebarCollapsed,
} from '../../utils/navigation/dashboardSidebarLayout';
import {
  formatSidebarActionCountBadge,
  formatSidebarActionCountLabel,
  getSidebarActionCount,
  publishSidebarActionCount,
  subscribeSidebarActionCount,
} from '../../utils/navigation/sidebarActionCount';
import { openDashboardSidebar } from '../../utils/navigation/openDashboardSidebar';
import { useIncompleteActivationCount } from '../../hooks/shared/useActivationSidebarTasks';
import { getKnownUserType, getUserTypeFromToken } from '../tours/tourUserType';
import useDashboardSidebarToggleLabel from './useDashboardSidebarToggleLabel';
import theme, { FONT_SIZES } from '../../Theme';

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Poppins', 'Arial', sans-serif;
  font-size: ${FONT_SIZES.body};
  font-weight: 600;
  line-height: 1;
  color: ${theme.palette.text.primary};
  flex-shrink: 0;
  min-width: 0;
  max-width: min(180px, 42vw);
  overflow: visible;

  &:hover {
    color: ${theme.palette.text.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.primary.main};
    outline-offset: 2px;
    border-radius: 6px;
  }

  svg {
    width: 22px;
    height: 22px;
    stroke: currentColor;
    flex-shrink: 0;
  }
`;

const ToggleLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  overflow: visible;
  padding: 4px 8px 0 0;
`;

const ActionCountBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: ${theme.palette.accent.main};
  color: ${theme.palette.primary.contrastText};
  font-size: ${FONT_SIZES.micro};
  font-weight: 700;
  line-height: 1;
  border: 1px solid ${theme.palette.background.paper};
`;

function ActionNeededBadge({ count }) {
  if (!count) return null;
  return (
    <ActionCountBadge aria-hidden>
      {formatSidebarActionCountBadge(count)}
    </ActionCountBadge>
  );
}

function DashboardSidebarToggle({ userType: userTypeProp }) {
  const isDesktop = useMediaQuery(DASHBOARD_SIDEBAR_DESKTOP_MQL, { noSsr: true });
  const userType = getKnownUserType(userTypeProp) || getUserTypeFromToken();
  const label = useDashboardSidebarToggleLabel(userType);
  const [desktopCollapsed, setDesktopCollapsed] = useState(readDashboardSidebarCollapsed);
  const [actionCount, setActionCount] = useState(getSidebarActionCount);
  const activationCount = useIncompleteActivationCount(userType);
  const displayCount = actionCount > 0 ? actionCount : activationCount;

  useEffect(() => {
    const onState = (event) => {
      setDesktopCollapsed(Boolean(event.detail?.collapsed));
    };
    window.addEventListener(MILLET_DESKTOP_SIDEBAR_STATE_EVENT, onState);
    return () => window.removeEventListener(MILLET_DESKTOP_SIDEBAR_STATE_EVENT, onState);
  }, []);

  useEffect(() => subscribeSidebarActionCount(setActionCount), []);

  useEffect(() => {
    if (activationCount > 0 && getSidebarActionCount() < 1) {
      publishSidebarActionCount(activationCount);
    }
  }, [activationCount]);

  const attentionLabel = displayCount > 0 ? formatSidebarActionCountLabel(displayCount) : null;
  const showCollapsedDesktop = isDesktop && desktopCollapsed;

  if (isDesktop && !desktopCollapsed) return null;

  let accessibleName = 'Open dashboard menu';
  let handleClick = openDashboardSidebar;
  if (showCollapsedDesktop) {
    accessibleName = 'Expand navigation sidebar';
    handleClick = expandDesktopDashboardSidebar;
  }
  if (attentionLabel) {
    accessibleName = `${accessibleName}, ${attentionLabel}`;
  }

  const showLabel = Boolean(label) && !showCollapsedDesktop;

  const toggleButton = (
    <ToggleButton
      onClick={handleClick}
      aria-label={accessibleName}
      type="button"
    >
      <IconWrap>
        <LuPanelLeft aria-hidden strokeWidth={2} />
        <ActionNeededBadge count={displayCount} />
      </IconWrap>
      {showLabel ? <ToggleLabel>{label}</ToggleLabel> : null}
    </ToggleButton>
  );

  if (!showCollapsedDesktop) return toggleButton;

  return (
    <Tooltip title={`${accessibleName} ([)`} placement="bottom" enterDelay={400}>
      {toggleButton}
    </Tooltip>
  );
}

export default DashboardSidebarToggle;
